import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { isDoorVisitCustomDateRangeValid, type DoorVisitFilterState } from './filters/doorVisitFilters';
import { buildCreateDoorVisitReportVariables, type DoorVisitReportApi, type DoorVisitReportRun } from './doorVisitReportApi';
import { DoorVisitReportDownloadButton } from './DoorVisitReportDownloadButton';
import { getDoorVisitReportStatusText, getDoorVisitReportTerminalAnnouncement } from './doorVisitReportStatus';
import { useDoorVisitReportPolling } from './useDoorVisitReportPolling';
import { PageControlsBar } from './PageControlsBar';
import { DoorVisitReportStatus } from '../graphql/__generated__/graphql';

export type DoorVisitReportRequestIdGenerator = () => string;

export const generateDoorVisitReportRequestId: DoorVisitReportRequestIdGenerator = () => crypto.randomUUID();

export type DoorVisitReportControlsProps = {
  filters: DoorVisitFilterState;
  api: DoorVisitReportApi;
  disabled?: boolean;
  requestIdGenerator?: DoorVisitReportRequestIdGenerator;
};

export const DoorVisitReportControls = ({
  filters,
  api,
  disabled = false,
  requestIdGenerator = generateDoorVisitReportRequestId,
}: DoorVisitReportControlsProps) => {
  const customRangeInvalid = filters.dateRange === 'custom' &&
    !isDoorVisitCustomDateRangeValid(filters.fromDate, filters.throughDate);
  const exportDisabled = disabled || customRangeInvalid;
  const [report, setReport] = useState<DoorVisitReportRun | null>(null);
  const [submissionRequestId, setSubmissionRequestId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [pollingError, setPollingError] = useState<string | null>(null);
  const [terminalAnnouncement, setTerminalAnnouncement] = useState('');
  const announcedTerminalsRef = useRef(new Set<string>());

  const announceTerminal = (announcement: string, terminalReport: DoorVisitReportRun): void => {
    const announcementKey = `${terminalReport.id}:${terminalReport.attempt}:${terminalReport.status}`;
    if (announcedTerminalsRef.current.has(announcementKey)) return;
    announcedTerminalsRef.current.add(announcementKey);
    setTerminalAnnouncement(announcement);
  };

  const { refreshing, refresh } = useDoorVisitReportPolling({
    report,
    api,
    onReportChange: nextReport => {
      setReport(nextReport);
      setPollingError(null);
    },
    onAutomaticError: () => setPollingError('Could not refresh report status. Use Refresh status to try again.'),
    onTerminal: announceTerminal,
  });

  const submitReport = async (): Promise<void> => {
    if (submitting || exportDisabled) return;

    const requestId = submissionRequestId ?? requestIdGenerator();
    setSubmissionRequestId(requestId);
    setSubmitting(true);
    setSubmissionError(null);

    try {
      const acceptedReport = await api.createReport(buildCreateDoorVisitReportVariables(filters, requestId));
      setReport(acceptedReport);
      const terminalAnnouncement = getDoorVisitReportTerminalAnnouncement(acceptedReport);
      if (terminalAnnouncement !== null) announceTerminal(terminalAnnouncement, acceptedReport);
      // Only an accepted request can retire the retained idempotency key. A
      // later export generates a fresh UUID; uncertain failures retry this one.
      setSubmissionRequestId(null);
      setPollingError(null);
    } catch {
      setSubmissionError('Could not submit the report. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageControlsBar label="Door Visit reports">
        <Button
          variant="contained"
          onClick={() => void submitReport()}
          disabled={submitting || exportDisabled}
          aria-busy={submitting}
        >
          {submitting && <CircularProgress size={16} aria-hidden />} Export XLSX
        </Button>
        <Button component={Link} to="/door-visits/report-runs" variant="outlined">
          View report runs
        </Button>
        {report !== null && (
          <Stack spacing={0.5} sx={{ minWidth: { sm: 220 } }}>
            {refreshing && <Typography variant="caption">Refreshing report status…</Typography>}
            <DoorVisitReportDownloadButton
              report={report}
              api={api}
              accessibleName="Download current Door Visit report"
              showGeneratingState
            />
            {report.status === DoorVisitReportStatus.Failed && (
              <Typography variant="body2" aria-label={`Current report status: ${getDoorVisitReportStatusText(report)}`}>
                {getDoorVisitReportStatusText(report)}
              </Typography>
            )}
          </Stack>
        )}
        {pollingError !== null && (
          <Button variant="outlined" onClick={refresh} disabled={refreshing} aria-busy={refreshing}>
            Refresh status
          </Button>
        )}
      </PageControlsBar>
      {submissionError !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submissionError}
        </Alert>
      )}
      {pollingError !== null && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {pollingError}
        </Alert>
      )}
      <Box
        component="span"
        aria-live="polite"
        aria-atomic="true"
        sx={{ position: 'absolute', width: 1, height: 1, p: 0, m: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}
      >
        {terminalAnnouncement}
      </Box>
    </>
  );
};
